/** Payments domain events — catalog 1.5.0 additions. */
import {
  asEventRecord,
  defineEvent,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface PaymentTenderEventPayload {
  orgId: string;
  tenderId: string;
  orderId: string;
}

export interface PaymentWebhookReceivedPayload {
  orgId: string;
  webhookEventId: string;
  provider: string;
}

const tenderSchema = (type: string): EventSchema<PaymentTenderEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    return {
      orgId: requireString(record.orgId, 'orgId', type),
      tenderId: requireString(record.tenderId, 'tenderId', type),
      orderId: requireString(record.orderId, 'orderId', type),
    };
  },
});

const webhookSchema: EventSchema<PaymentWebhookReceivedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'payments:webhook-received');
    return {
      orgId: requireString(record.orgId, 'orgId', 'payments:webhook-received'),
      webhookEventId: requireString(
        record.webhookEventId,
        'webhookEventId',
        'payments:webhook-received',
      ),
      provider: requireString(record.provider, 'provider', 'payments:webhook-received'),
    };
  },
};

const definePaymentEvent = <T>(
  type: string,
  description: string,
  schema: EventSchema<T>,
): EventDefinition<T> =>
  defineEvent<T>({
    type,
    version: 1,
    schema,
    auditable: true,
    description,
    since: '1.5.0',
  });

export const PAYMENTS_TENDER_AUTHORIZED_EVENT = definePaymentEvent(
  'payments:tender-authorized',
  'A payment tender was authorized.',
  tenderSchema('payments:tender-authorized'),
);
export const PAYMENTS_TENDER_CAPTURED_EVENT = definePaymentEvent(
  'payments:tender-captured',
  'A payment tender was captured.',
  tenderSchema('payments:tender-captured'),
);
export const PAYMENTS_TENDER_FAILED_EVENT = definePaymentEvent(
  'payments:tender-failed',
  'A payment tender failed.',
  tenderSchema('payments:tender-failed'),
);
export const PAYMENTS_TENDER_REFUNDED_EVENT = definePaymentEvent(
  'payments:tender-refunded',
  'A payment tender was refunded.',
  tenderSchema('payments:tender-refunded'),
);
export const PAYMENTS_WEBHOOK_RECEIVED_EVENT = definePaymentEvent(
  'payments:webhook-received',
  'A provider webhook was received for a tenant.',
  webhookSchema,
);

export const PAYMENTS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [PAYMENTS_TENDER_AUTHORIZED_EVENT.type]: PAYMENTS_TENDER_AUTHORIZED_EVENT,
  [PAYMENTS_TENDER_CAPTURED_EVENT.type]: PAYMENTS_TENDER_CAPTURED_EVENT,
  [PAYMENTS_TENDER_FAILED_EVENT.type]: PAYMENTS_TENDER_FAILED_EVENT,
  [PAYMENTS_TENDER_REFUNDED_EVENT.type]: PAYMENTS_TENDER_REFUNDED_EVENT,
  [PAYMENTS_WEBHOOK_RECEIVED_EVENT.type]: PAYMENTS_WEBHOOK_RECEIVED_EVENT,
};
